import { useMemo, useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, ArrowLeft, Loader2, Plus, RefreshCcw, Search, Trash2 } from 'lucide-react';
import { api, LitLegionTaskStatusResponse } from '@/lib/api';
import { useAdminUserTaskStatuses } from '../hooks/useAdminUserTaskStatuses';
import { useAdminTasks } from '../hooks/useAdminTasks';
import { LL_TASK_STATUSES, isLLTaskStatus } from '../types';

export function AdminUserTaskStatusesPage() {
  const {
    statuses,
    total,
    loading,
    error,
    loadedUsername,
    resolvedUserId,
    fetchForUsername,
    refresh,
    loadMore,
    hasMore,
    applyLocalUpdate,
    applyLocalDelete,
    applyLocalAdd,
  } = useAdminUserTaskStatuses();

  const { tasks: allTasks, loading: tasksLoading } = useAdminTasks();

  const [usernameInput, setUsernameInput] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [addTaskId, setAddTaskId] = useState('');
  const [addStatus, setAddStatus] = useState('available');
  const [adding, setAdding] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeMessage, setRecomputeMessage] = useState<string | null>(null);

  // Tasks already on the user are shown grayed out / blocked in the picker.
  // The server enforces the unique (user_id, task_id) constraint, but this
  // saves a round-trip and avoids a confusing 409 message.
  const assignedTaskIds = useMemo(() => new Set(statuses.map((s) => s.taskId)), [statuses]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = usernameInput.trim();
    if (!trimmed) return;
    setActionError(null);
    fetchForUsername(trimmed);
  };

  const handleStatusChange = async (taskStatus: LitLegionTaskStatusResponse, newStatus: string) => {
    if (!isLLTaskStatus(newStatus) || newStatus === taskStatus.status) return;

    // Admin override bypasses the player state machine: setting status to
    // `awarded` does NOT grant XP, coin rewards, or badge rewards (those only
    // fire from the player flow). Make sure the admin understands that
    // before we let them put the player into a state that looks earned.
    if (newStatus === 'awarded' || newStatus === 'completed') {
      const proceed = window.confirm(
        `Setting status to "${newStatus}" via admin override does NOT grant XP, ` +
          `coin rewards, or badge rewards. The player will see the task as ` +
          `${newStatus} but will not receive any rewards from this change.\n\n` +
          `Continue?`,
      );
      if (!proceed) return;
    }

    setUpdatingId(taskStatus.taskStatusId);
    setActionError(null);
    try {
      const result = await api.adminUpdateLitLegionTaskStatus(taskStatus.taskStatusId, newStatus);
      applyLocalUpdate(result.taskStatus);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update task status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (taskStatus: LitLegionTaskStatusResponse) => {
    const confirmed = window.confirm(
      `Delete task status ${taskStatus.taskStatusId}? This cannot be undone. To re-add availability after a delete, use the Recompute availability button.`,
    );
    if (!confirmed) return;

    setDeletingId(taskStatus.taskStatusId);
    setActionError(null);
    try {
      await api.adminDeleteLitLegionTaskStatus(taskStatus.taskStatusId);
      applyLocalDelete(taskStatus.taskStatusId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete task status');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRecompute = async () => {
    if (!resolvedUserId) return;
    setRecomputing(true);
    setActionError(null);
    setRecomputeMessage(null);
    try {
      const result = await api.adminRecomputeLitLegionAvailability(resolvedUserId);
      if (result.count === 0) {
        setRecomputeMessage(
          'No tasks needed availability — every unblocked task is already assigned.',
        );
      } else {
        setRecomputeMessage(
          `Added ${result.count} 'available' task${result.count === 1 ? '' : 's'} for this user.`,
        );
        refresh();
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to recompute availability');
    } finally {
      setRecomputing(false);
    }
  };

  const handleAddStatus = async (e: FormEvent) => {
    e.preventDefault();
    if (!resolvedUserId || !addTaskId || !isLLTaskStatus(addStatus)) return;

    if (assignedTaskIds.has(addTaskId)) {
      setActionError(
        'This task is already assigned to the user. Edit its existing status instead.',
      );
      return;
    }

    setAdding(true);
    setActionError(null);
    try {
      const result = await api.adminAssignLitLegionTaskToUser(resolvedUserId, addTaskId, addStatus);
      applyLocalAdd(result.taskStatus);
      setAddTaskId('');
      setAddStatus('available');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add task status');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_lit_legion/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin Dashboard
        </Link>
        <h1 className="text-2xl font-semibold">User Task Statuses</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Look up a user's task statuses and override or remove them individually.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          placeholder="Enter username (e.g. devuser1)"
          value={usernameInput}
          onChange={(e) => setUsernameInput(e.target.value)}
          className="max-w-md"
        />
        <Button type="submit" disabled={loading || !usernameInput.trim()}>
          {loading && !loadedUsername ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Look up
        </Button>
        {loadedUsername && (
          <Button type="button" variant="outline" onClick={refresh} disabled={loading}>
            Refresh
          </Button>
        )}
        {loadedUsername && resolvedUserId && (
          <Button
            type="button"
            variant="outline"
            onClick={handleRecompute}
            disabled={loading || recomputing}
            title="Re-evaluate every task and add 'available' rows for any that are unblocked but missing"
          >
            {recomputing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4 mr-2" />
            )}
            Recompute availability
          </Button>
        )}
      </form>
      {recomputeMessage && <p className="text-sm text-muted-foreground">{recomputeMessage}</p>}

      {error && (
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      )}

      {actionError && <p className="text-sm text-destructive">{actionError}</p>}

      {loadedUsername && resolvedUserId && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="h-4 w-4" /> Add task status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddStatus} className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[280px] space-y-1">
                <label htmlFor="add-task" className="text-xs text-muted-foreground">
                  Task
                </label>
                <Select value={addTaskId} onValueChange={setAddTaskId}>
                  <SelectTrigger id="add-task">
                    <SelectValue placeholder={tasksLoading ? 'Loading tasks…' : 'Pick a task'} />
                  </SelectTrigger>
                  <SelectContent>
                    {allTasks.map((t) => {
                      const already = assignedTaskIds.has(t.taskId);
                      return (
                        <SelectItem key={t.taskId} value={t.taskId} disabled={already}>
                          {t.name}
                          {already ? ' (already assigned)' : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[160px] space-y-1">
                <label htmlFor="add-status" className="text-xs text-muted-foreground">
                  Status
                </label>
                <Select value={addStatus} onValueChange={setAddStatus}>
                  <SelectTrigger id="add-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LL_TASK_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={!addTaskId || adding}>
                {adding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Add
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-2">
              Bypasses the player state machine and grants no rewards. Pick `available` to let the
              player start the task; pick `awarded` only when reconciling a previously-completed
              quest.
            </p>
          </CardContent>
        </Card>
      )}

      {loadedUsername && (
        <div>
          <p className="text-sm text-muted-foreground mb-3">
            {total} status{total !== 1 ? 'es' : ''} for{' '}
            <span className="font-medium text-foreground">{loadedUsername}</span>
            {resolvedUserId && <span className="font-mono text-xs ml-2">({resolvedUserId})</span>}
          </p>

          {loading && statuses.length === 0 ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-lg" />
              ))}
            </div>
          ) : statuses.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">
              No task statuses found for this user.
            </p>
          ) : (
            <div className="space-y-3">
              {statuses.map((s) => (
                <Card key={s.taskStatusId}>
                  <CardContent className="flex items-center justify-between gap-4 py-4">
                    <div className="flex-1 min-w-0 text-sm space-y-1">
                      {s.task ? (
                        <>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium truncate">{s.task.name}</p>
                            {s.task.taskType && (
                              <Badge variant="secondary" className="text-xs shrink-0">
                                {s.task.taskType}
                              </Badge>
                            )}
                            {s.task.category && (
                              <Badge variant="outline" className="text-xs shrink-0">
                                {s.task.category}
                              </Badge>
                            )}
                          </div>
                          {s.task.description && (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {s.task.description}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="font-medium text-muted-foreground italic">(task deleted)</p>
                      )}
                      <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground">
                        {s.completedAt && (
                          <span>completed: {new Date(s.completedAt).toLocaleString()}</span>
                        )}
                        {s.xpAmountReceived !== null && <span>XP: {s.xpAmountReceived}</span>}
                        <span>updated: {new Date(s.updatedAt).toLocaleString()}</span>
                      </div>
                      {s.userInput && (
                        <p className="text-xs text-muted-foreground">
                          input: <span className="text-foreground">{s.userInput}</span>
                        </p>
                      )}
                      <p className="font-mono text-[10px] text-muted-foreground/70 truncate">
                        {s.taskStatusId} · task {s.taskId}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Select
                        value={s.status}
                        onValueChange={(value) => handleStatusChange(s, value)}
                        disabled={updatingId === s.taskStatusId}
                      >
                        <SelectTrigger
                          className="w-[140px]"
                          aria-label={`Status for ${s.task?.name ?? s.taskId}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LL_TASK_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(s)}
                        disabled={deletingId === s.taskStatusId}
                      >
                        {deletingId === s.taskStatusId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {hasMore && (
                <div className="text-center">
                  <Button variant="ghost" onClick={loadMore} disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load more'
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
